import { ChangeDetectorRef, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NumberFormattingService } from '@capital-access/common/formatting';

function getDecimalPlaces(num: number): number {
  const str = num.toString();
  if (str.indexOf('.') === -1) return 0;
  return str.split('.')[1].length;
}

// Handle both period and comma as decimal separators for different locales
function removeTrailingZeros(value: string): string {
  if (value.includes('.') || value.includes(',')) {
    const match = value.match(/^(.+?)([.,]\d+?)0*$/);
    if (match) {
      let result = match[1] + match[2];
      if (result.endsWith('.') || result.endsWith(',')) {
        result = result.slice(0, -1);
      }
      return result;
    }
  }
  return value;
}

/**
 * Format number with caching
 */
export function formatNumberWithCache(
  value: unknown,
  decimalPlaces: number,
  cachePrefix: string,
  numberFormattingService: NumberFormattingService,
  destroyRef: DestroyRef,
  cdr: ChangeDetectorRef,
  formattedCache: Map<string, string>
): string {
  const numValue = typeof value === 'number' ? value : Number(value);
  if (isNaN(numValue)) return '';

  const cacheKey = `${cachePrefix}_${numValue}`;

  // Check cache first
  if (formattedCache.has(cacheKey)) {
    return formattedCache.get(cacheKey)!;
  }

  let maxFractionDigits = decimalPlaces;

  if (cachePrefix.toLowerCase() === 'volume') {
    // Determine required decimal places for smallest value
    const minValueDecimalPlaces = numValue !== Number.POSITIVE_INFINITY ? getDecimalPlaces(numValue) : 0;

    // Use the greater of majorUnit decimal places or minValue decimal places, up to 6
    maxFractionDigits = minValueDecimalPlaces;
  }

  // Use fallback while formatting asynchronously
  const fallback = numValue.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits
  });

  // Format using NumberFormattingService with takeUntilDestroyed pattern
  numberFormattingService
    .formatNumber(numValue, maxFractionDigits)
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe(formatted => {
      const trimmed = removeTrailingZeros(formatted);
      formattedCache.set(cacheKey, trimmed);
      cdr.detectChanges();
    });

  return fallback;
}
